import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authAPI } from '../services/api';

/**
 * Auth Store - SINGLE SOURCE OF TRUTH for authentication
 *
 * Key design decisions:
 * 1. Persist BOTH user AND isAuthenticated to avoid race conditions
 * 2. isInitialized starts FALSE and only becomes TRUE after verification
 * 3. All auth state changes go through this store ONLY
 */

const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      isAuthenticated: false,
      isInitialized: false,
      isLoading: false,
      error: null,

      setAuth: (token, user) => {
        console.log('[Auth] setAuth called');

        // Save token
        localStorage.setItem('authToken', token);
        localStorage.setItem('lastActivity', Date.now().toString());

        // Update store state
        set({
          user,
          isAuthenticated: true,
          isInitialized: true,
          isLoading: false,
          error: null,
        });
      },

      // Login - called when user submits credentials
      login: async (credentials) => {
        console.log('[Auth] login attempt');
        set({ isLoading: true, error: null });

        try {
          const response = await authAPI.login(credentials);
          const { success, token } = response.data;

          if (success && token) {
            localStorage.setItem('authToken', token);
            localStorage.setItem('lastActivity', Date.now().toString());
            console.log('[Auth] login SUCCESS - token saved, fetching full profile...');

            // Always fetch the full profile after login so profile.account_type
            // (enterprise_admin, etc.) is included — same as initializeAuth on page refresh.
            try {
              const profileResponse = await authAPI.getProfile();
              set({
                user: profileResponse.data,
                isAuthenticated: true,
                isLoading: false,
                isInitialized: true,
                error: null
              });
            } catch {
              // Fallback to login response user if getProfile fails
              set({
                user: response.data.user,
                isAuthenticated: true,
                isLoading: false,
                isInitialized: true,
                error: null
              });
            }

            return { success: true };
          } else {
            const message = response.data?.error || 'Login failed';
            console.log('[Auth] login FAILED -', message);
            set({ error: message, isLoading: false });
            return { success: false, error: message };
          }
        } catch (error) {
          const message = error.response?.data?.error || error.response?.data?.message || 'Login failed';
          console.log('[Auth] login ERROR -', message);
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },


      // Register - create new account
      register: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.register(data);
          set({ isLoading: false });
          return { success: true, data: response.data };
        } catch (error) {
          const message = error.response?.data?.error || error.response?.data?.message || 'Registration failed';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      // Logout - clear all auth state
      logout: async () => {
        console.log('[Auth] logout');
        try {
          await authAPI.logout();
        } catch (e) {
          // Ignore logout API errors
        }
        localStorage.removeItem('authToken');
        localStorage.removeItem('lastActivity');
        set({
          user: null,
          isAuthenticated: false,
          error: null
        });
      },

      // Initialize - called ONCE on app start to verify stored auth
      initializeAuth: async () => {
        const token = localStorage.getItem('authToken');
        const currentState = get();

        console.log('[Auth] initializeAuth - token exists:', !!token, 'user exists:', !!currentState.user);

        // No token = definitely not authenticated
        if (!token) {
          console.log('[Auth] No token, clearing auth state');
          set({
            user: null,
            isAuthenticated: false,
            isInitialized: true
          });
          return;
        }

        // Have token - verify it's still valid with the server
        try {
          console.log('[Auth] Verifying token with server...');
          const response = await authAPI.getProfile();
          console.log('[Auth] Token valid, user:', response.data?.username);
          set({
            user: response.data,
            isAuthenticated: true,
            isInitialized: true
          });
        } catch (error) {
          // Token invalid or expired
          console.log('[Auth] Token invalid, clearing auth state');
          localStorage.removeItem('authToken');
          set({
            user: null,
            isAuthenticated: false,
            isInitialized: true
          });
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      // Persist BOTH user AND isAuthenticated
      // This prevents the flash of unauthenticated state on reload
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated
      }),
    }
  )
);

export default useAuthStore;
